import React, { useEffect } from 'react';
import { apiClient } from './apiClient';
import { signOut as amplifySignOut } from 'aws-amplify/auth';
import { shareInFlightRequest } from './sharedApiRequest';


export type DbStatusCheckerProps = {
  signOut?: (data?: any) => void;
  onUnavailable?: (status?: number) => void;
  onAvailable?: () => void;
  onRetrying?: (isRetrying: boolean) => void;
  retryCount?: number;
  retryDelayMs?: number;
};

/**
 * DBステータスチェック用のサブコンポーネント
 * - アプリ表示直後に /db-status-check を叩き、一定回数リトライ後に未復旧なら signOut
 * - 一度だけチェック（hasCheckedRef）し、再レンダリング時の重複実行を防止
 */
export const DbStatusChecker: React.FC<DbStatusCheckerProps> = ({
  signOut,
  onUnavailable,
  onAvailable,
  onRetrying,
  retryCount = 3,
  retryDelayMs = 5000,
}) => {
  const hasCheckedRef = React.useRef(false);
  const signOutRef = React.useRef(signOut);
  const onUnavailableRef = React.useRef(onUnavailable);
  const onAvailableRef = React.useRef(onAvailable);
  const onRetryingRef = React.useRef(onRetrying);
  const retryCountRef = React.useRef(3);
  const retryDelayMsRef = React.useRef(5000);

  // 最新の signOut / onUnavailable を参照保持（関数インスタンスの入れ替わりに追従）
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    onUnavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  useEffect(() => {
    onAvailableRef.current = onAvailable;
  }, [onAvailable]);

  useEffect(() => {
    onRetryingRef.current = onRetrying;
  }, [onRetrying]);

  useEffect(() => {
    retryCountRef.current = retryCount;
  }, [retryCount]);

  useEffect(() => {
    retryDelayMsRef.current = retryDelayMs;
  }, [retryDelayMs]);

  useEffect(() => {
    if (hasCheckedRef.current) return;
    // signOut が未提供でも後段で Amplify の signOut をフォールバック実行するため続行
    hasCheckedRef.current = true;
    let cancelled = false;

    (async () => {
      let notifiedUnavailable = false;
      let notifiedRetrying = false;
      const setRetrying = (next: boolean) => {
        if (cancelled) return;
        if (next === notifiedRetrying) return;
        notifiedRetrying = next;
        try { onRetryingRef.current?.(next); } catch {}
      };
      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

      try {
        const maxAttempts = Math.max(1, retryCountRef.current + 1);
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          if (cancelled) return;
          const isLast = attempt === maxAttempts;
          try {
            const resp = await shareInFlightRequest(
              'db-status-check',
              () => apiClient.get('/db-status-check', { validateStatus: () => true }),
            );
            if (cancelled) return;
            const unavailable =
              resp.status === 503 ||
              (resp.status >= 500 && resp.status < 600) ||
              (resp?.data && (resp.data.ok === false || resp.data.available === false));

            if (!unavailable) {
              setRetrying(false);
              try { onAvailableRef.current?.(); } catch {}
              return;
            }

            console.log('DB connection failed or unavailable!');
            setRetrying(true);
            if (!notifiedUnavailable) {
              notifiedUnavailable = true;
              // ログイン画面での文言表示用フック（あれば呼ぶ）
              try { onUnavailableRef.current?.(resp.status); } catch {}
            }

            if (isLast) {
              setRetrying(false);
              // 再接続を待っても復旧しない場合のみログアウト
              try {
                if (signOutRef.current) {
                  // Authenticator 由来の signOut があればそれを優先
                  signOutRef.current();
                } else {
                  // なければ Amplify の signOut にフォールバック
                  await amplifySignOut();
                }
              } catch {
                // 念のためフォールバックで二重に失敗しないように握りつぶす
                try { await amplifySignOut(); } catch {}
              }
              return;
            }
          } catch {
            if (cancelled) return;
            // 通信エラー時もログイン抑止用に通知だけは行う
            setRetrying(true);
            if (!notifiedUnavailable) {
              notifiedUnavailable = true;
              try { onUnavailableRef.current?.(); } catch {}
            }
            if (isLast) {
              setRetrying(false);
              try {
                if (signOutRef.current) {
                  signOutRef.current();
                } else {
                  await amplifySignOut();
                }
              } catch {
                try { await amplifySignOut(); } catch {}
              }
              return;
            }
          }

          // Full-jitter exponential backoff prevents every browser from
          // retrying a recovering database at the same instant.
          const backoffWindowMs = Math.min(
            retryDelayMsRef.current * (2 ** (attempt - 1)),
            30_000,
          );
          await sleep(Math.floor(Math.random() * backoffWindowMs));
        }
      } catch {
        // no-op
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
};
